import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

export interface CustomerHealth {
  id: string;
  customer_id: string;
  tickets_signal: number | null;
  sla_signal: number | null;
  uptime_signal: number | null;
  /** 0–100 over the signals that have data; null when none do. */
  operational_score: number | null;
  computed_at: string;
}

/**
 * Operational health rows for the account scope.
 *
 * The customer surface exposes operational signals only — mrr_signal and
 * renewal_signal are structurally absent from
 * customer_health_scores_customer_view. Returns one row per account that
 * has been scored; accounts without a row have "not enough data".
 */
/** health-score-recompute runs hourly; two misses before it reads as stale. */
export const HEALTH_EXPECTED_REFRESH_MS = 2 * 60 * 60 * 1000;
export const HEALTH_STALE_MS = 6 * 60 * 60 * 1000;

export function useCustomerHealthScores() {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['customer-health-scores', scope.key],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<CustomerHealth[]> => {
      const { data, error } = await supabase
        .from('customer_health_scores_customer_view')
        .select(
          'id, customer_id, tickets_signal, sla_signal, uptime_signal, operational_score, computed_at',
        )
        .in('customer_id', scope.ids)
        .order('computed_at', { ascending: false });
      if (error) throw error;
      // Keep the latest row per account.
      const seen = new Set<string>();
      const latest: CustomerHealth[] = [];
      for (const row of (data ?? []) as unknown as CustomerHealth[]) {
        if (seen.has(row.customer_id)) continue;
        seen.add(row.customer_id);
        latest.push(row);
      }
      return latest;
    },
    staleTime: 5 * 60 * 1000,
  });
}
